import type { ReactNode } from 'react'

/**
 * Безопасный рендер ответа Штаба (S13): абзацы, списки, жирный и `код`.
 * Текст никогда не вставляется как HTML - только строками в React-элементы,
 * поэтому разметка в ответе модели или в вопросе остаётся текстом.
 */

// Маркеры списка: дефис, звёздочка и типографская точка (U+2022) от модели
const BULLET = /^\s*(?:[-*\u2022])\s+(.*)$/
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/
const HEADING = /^\s*#{1,6}\s+(.*)$/

/** **жирный**, __жирный__ и `код` внутри строки. */
function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = []
  const re = /\*\*(.+?)\*\*|__(.+?)__|`([^`]+)`/g
  let last = 0
  let m: RegExpExecArray | null
  let i = 0
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const bold = m[1] ?? m[2]
    if (bold !== undefined) out.push(<strong key={`${keyBase}-${i++}`}>{bold}</strong>)
    else out.push(<code key={`${keyBase}-${i++}`}>{m[3]}</code>)
    last = m.index + m[0].length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'h'; text: string }
  | { kind: 'ul' | 'ol'; items: string[] }

function parse(text: string): Block[] {
  const blocks: Block[] = []
  // Текущий блок - всегда последний в списке, пока open
  let open = false
  const last = () => (open ? blocks[blocks.length - 1] : undefined)

  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trimEnd()
    if (!line.trim()) {
      open = false
      continue
    }
    const heading = HEADING.exec(line)
    const bullet = BULLET.exec(line)
    const numbered = bullet ? null : NUMBERED.exec(line)
    const cur = last()
    if (heading) {
      blocks.push({ kind: 'h', text: heading[1] })
      open = false
    } else if (bullet || numbered) {
      const kind = bullet ? 'ul' : 'ol'
      const item = (bullet ?? numbered)![1]
      if (cur && cur.kind === kind) cur.items.push(item)
      else {
        blocks.push({ kind, items: [item] })
        open = true
      }
    } else if (cur && cur.kind === 'p') {
      cur.lines.push(line.trim())
    } else {
      blocks.push({ kind: 'p', lines: [line.trim()] })
      open = true
    }
  }
  return blocks
}

export function RichText({ text }: { text: string }) {
  return (
    <div className="pc-rich">
      {parse(text).map((b, bi) => {
        const key = `b${bi}`
        if (b.kind === 'h') {
          return (
            <p key={key}>
              <strong>{b.text.replace(/\*\*/g, '')}</strong>
            </p>
          )
        }
        if (b.kind === 'p') {
          return (
            <p key={key}>
              {b.lines.flatMap((l, li) => (li === 0 ? inline(l, `${key}-${li}`) : [<br key={`${key}-br${li}`} />, ...inline(l, `${key}-${li}`)]))}
            </p>
          )
        }
        const List = b.kind
        return (
          <List key={key}>
            {b.items.map((item, ii) => (
              <li key={ii}>{inline(item, `${key}-${ii}`)}</li>
            ))}
          </List>
        )
      })}
    </div>
  )
}
